/**
 * Column Layout Designer (super-admin)
 *
 * Reorder, relabel, realign, and toggle per-output (browser / print / PDF)
 * visibility of every column in the estimate output tables — the BOM and the
 * Product Placement Guide (panels + trims) — per retailer or universally.
 *
 * Columns are the live render-model columns (built-ins + the Trusscore SKU
 * column + any custom-field columns). With no saved layout a (retailer, scope)
 * uses the built-in default order — identical to legacy output. A live preview
 * renders the working layout with sample data through the same RenderTable the
 * real estimate uses.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowUp, ArrowDown, Save, RotateCcw, Eye, GripVertical, Barcode, Trash2 } from 'lucide-react';
import { BarcodeSymbology, ColumnAlign, ColumnLayoutItem, ColumnLayoutScope, CustomFieldColumnConfig, MaterialItem, Product, ProductCustomField } from '../../types';
import { BARCODE_SYMBOLOGIES } from '../../utils/barcode';
import {
  buildBomTable,
  buildPlacementTable,
  defaultColumnsForScope,
  ColumnMeta,
  RenderCtx,
  BUILTIN_CODE_SOURCES,
} from '../../utils/renderModel';
import { RenderTable } from '../../components/results/RenderTable';
import { fetchProductsPayload } from '../../services/api';
import {
  AdminColumnLayout as AdminColumnLayoutRow,
  ColumnLayoutRetailerSummary,
  fetchColumnLayouts,
  saveColumnLayout,
  deleteColumnLayout,
} from '../../services/adminColumnLayoutsApi';

const SCOPES: { value: ColumnLayoutScope; label: string }[] = [
  { value: 'bom', label: 'Bill of Materials' },
  { value: 'placementPanels', label: 'Placement — Panels' },
  { value: 'placementTrims', label: 'Placement — Trims' },
];

const ALIGN_OPTIONS: { value: '' | ColumnAlign; label: string }[] = [
  { value: '', label: 'Default' },
  { value: 'left', label: 'Left' },
  { value: 'center', label: 'Center' },
  { value: 'right', label: 'Right' },
];

/** Build the working item list for a (retailer, scope): saved order first
 *  (preserved as-is so a retailer's columns aren't lost when edited from a
 *  different catalog context), then any new candidate columns appended. */
function mergeItems(saved: ColumnLayoutItem[] | undefined, candidates: ColumnMeta[]): ColumnLayoutItem[] {
  const out: ColumnLayoutItem[] = [];
  const used = new Set<string>();

  if (saved && saved.length > 0) {
    for (const it of [...saved].sort((a, b) => a.order - b.order)) {
      if (used.has(it.id)) continue; // dedup only
      out.push({ ...it });
      used.add(it.id);
    }
  }
  // Append candidate columns not already present (new columns), visible everywhere.
  for (const c of candidates) {
    if (used.has(c.layoutId)) continue;
    out.push({
      id: c.layoutId,
      order: out.length,
      headingOverride: null,
      align: null,
      visibleBrowser: true,
      visiblePrint: true,
      visiblePdf: true,
    });
  }
  return out.map((it, i) => ({ ...it, order: i }));
}

/** A few sample rows from the selected retailer's catalog for the live preview. */
function sampleRows(products: Product[]): MaterialItem[] {
  const picks = products.slice(0, 3);
  if (picks.length === 0) {
    return [
      { productNumber: 'TC1612', productID: 'W&CB12White', name: "Wall&CeilingBoard 12' White", color: 'White', quantity: 15, lengthFeet: 12, sqft: 16, type: 'WCB_Surface' },
      { productNumber: 'TC1616', productID: 'W&CB16White', name: "Wall&CeilingBoard 16' White", color: 'White', quantity: 6, lengthFeet: 16, sqft: 21.33, type: 'WCB_Surface' },
    ];
  }
  return picks.map((p, i) => ({
    productNumber: p.productNumber,
    productID: p.productID,
    name: p.name,
    color: p.color,
    quantity: [15, 6, 16][i] ?? 8,
    lengthFeet: p.lengthFeet,
    sqft: p.sqft,
    type: 'WCB_Surface',
  }));
}

export const AdminColumnLayout: React.FC = () => {
  const [retailers, setRetailers] = useState<ColumnLayoutRetailerSummary[]>([]);
  const [rows, setRows] = useState<AdminColumnLayoutRow[]>([]);
  const [retailerId, setRetailerId] = useState<number | null>(null); // null = Universal
  const [scope, setScope] = useState<ColumnLayoutScope>('bom');
  const [items, setItems] = useState<ColumnLayoutItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // Field config + sample products for the SELECTED retailer (Universal → use
  // retailer 1 / Trusscore). Drives candidate columns + the live preview so a
  // retailer's own custom fields appear here, addable/removable.
  const [fieldConfig, setFieldConfig] = useState<CustomFieldColumnConfig[]>([]);
  const [previewProducts, setPreviewProducts] = useState<Product[]>([]);
  const [availableFields, setAvailableFields] = useState<{ fieldKey: string; label: string }[]>([]);

  useEffect(() => {
    let cancelled = false;
    fetchProductsPayload(retailerId ?? 1)
      .then((res) => {
        if (cancelled) return;
        setFieldConfig(res.fieldConfig ?? []);
        setPreviewProducts(res.products ?? []);
        setAvailableFields(res.availableFields ?? []);
      })
      .catch(() => {
        if (cancelled) return;
        setFieldConfig([]);
        setPreviewProducts([]);
        setAvailableFields([]);
      });
    return () => { cancelled = true; };
  }, [retailerId]);

  const candidates = useMemo(() => defaultColumnsForScope(scope, fieldConfig), [scope, fieldConfig]);
  const metaById = useMemo(() => new Map(candidates.map((c) => [c.layoutId, c])), [candidates]);

  // A barcode column can encode the built-in product number OR ANY active field
  // (even one that isn't shown as a column) — just needs to be populated.
  const sourceFieldOptions = useMemo(() => {
    const opts: { key: string; label: string }[] = [{ key: 'productNumber', label: 'Product # (Trusscore SKU)' }];
    BUILTIN_CODE_SOURCES.forEach((c) => opts.push({ key: c.key, label: c.label }));
    availableFields.forEach((f) => opts.push({ key: f.fieldKey, label: `${f.label} (${f.fieldKey})` }));
    return opts;
  }, [availableFields]);

  const getFields = useCallback(
    (productID: string): ProductCustomField[] =>
      previewProducts.find((p) => p.productID === productID)?.customFields ?? [],
    [previewProducts],
  );

  const savedFor = useCallback(
    (rid: number | null, sc: ColumnLayoutScope): ColumnLayoutItem[] | undefined =>
      rows.find((r) => r.retailerId === rid && r.scope === sc)?.items,
    [rows],
  );

  // (Re)build the working list whenever the selection or loaded rows change.
  useEffect(() => {
    setItems(mergeItems(savedFor(retailerId, scope), candidates));
    setDirty(false);
  }, [retailerId, scope, candidates, savedFor]);

  useEffect(() => {
    fetchColumnLayouts()
      .then((res) => {
        setRetailers(res.retailers);
        setRows(res.layouts);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const update = (id: string, patch: Partial<ColumnLayoutItem>) => {
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...patch } : it)));
    setDirty(true);
    setNotice(null);
  };

  const addBarcodeColumn = () => {
    const id = `barcode:${Date.now().toString(36)}`;
    setItems((prev) => [
      ...prev,
      {
        id,
        order: prev.length,
        headingOverride: 'Barcode',
        align: 'center',
        visibleBrowser: true,
        visiblePrint: true,
        visiblePdf: true,
        barcode: { symbology: 'code128', sourceFieldKey: 'productNumber', heightPx: 36 },
      },
    ]);
    setDirty(true);
    setNotice(null);
  };

  const removeItem = (id: string) => {
    setItems((prev) => prev.filter((it) => it.id !== id).map((it, i) => ({ ...it, order: i })));
    setDirty(true);
    setNotice(null);
  };

  const updateBarcode = (id: string, patch: Partial<NonNullable<ColumnLayoutItem['barcode']>>) => {
    setItems((prev) =>
      prev.map((it) =>
        it.id === id
          ? {
              ...it,
              barcode: {
                symbology: 'code128',
                sourceFieldKey: 'productNumber',
                ...(it.barcode ?? {}),
                ...patch,
              },
            }
          : it,
      ),
    );
    setDirty(true);
    setNotice(null);
  };

  const move = (index: number, dir: -1 | 1) => {
    setItems((prev) => {
      const next = [...prev];
      const j = index + dir;
      if (j < 0 || j >= next.length) return prev;
      [next[index], next[j]] = [next[j], next[index]];
      return next.map((it, i) => ({ ...it, order: i }));
    });
    setDirty(true);
    setNotice(null);
  };

  // Drag-and-drop reordering (native HTML5, grip handle only).
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [overIndex, setOverIndex] = useState<number | null>(null);

  const moveItem = (from: number, to: number) => {
    setItems((prev) => {
      if (from === to || from < 0 || to < 0 || from >= prev.length || to >= prev.length) return prev;
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next.map((it, i) => ({ ...it, order: i }));
    });
    setDirty(true);
    setNotice(null);
  };

  const onRowDrop = (idx: number) => {
    if (dragIndex !== null) moveItem(dragIndex, idx);
    setDragIndex(null);
    setOverIndex(null);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const ordered = items.map((it, i) => ({ ...it, order: i }));
      const { layout } = await saveColumnLayout({ retailerId, scope, items: ordered });
      setRows((prev) => {
        const others = prev.filter((r) => !(r.retailerId === retailerId && r.scope === scope));
        return [...others, layout];
      });
      setDirty(false);
      setNotice('Layout saved.');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      await deleteColumnLayout(retailerId, scope);
      setRows((prev) => prev.filter((r) => !(r.retailerId === retailerId && r.scope === scope)));
      setItems(mergeItems(undefined, candidates));
      setDirty(false);
      setNotice('Reverted to the default layout.');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Reset failed');
    } finally {
      setSaving(false);
    }
  };

  // Live preview built through the real render model + RenderTable.
  const previewCtx: RenderCtx = useMemo(
    () => ({ currency: 'USD', showPrices: scope === 'bom', getFields }),
    [scope, getFields],
  );
  const previewTable = useMemo(() => {
    const layout = { scope, items };
    const rowsData = sampleRows(previewProducts);
    if (scope === 'bom') return buildBomTable(rowsData, true, previewCtx, fieldConfig, layout);
    return buildPlacementTable(rowsData, scope === 'placementPanels', previewCtx, fieldConfig, layout);
  }, [scope, items, previewCtx, fieldConfig, previewProducts]);

  const hasSaved = !!savedFor(retailerId, scope);

  if (loading) return <div className="p-4">Loading column layouts…</div>;

  return (
    <div className="admin-column-layout">
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
        <div>
          <h2 className="h4 mb-1">Column Layout Designer</h2>
          <p className="text-muted small mb-0">
            Reorder, relabel, and show/hide columns in the estimate output tables — per retailer or universally.
          </p>
        </div>
        <div className="d-flex gap-2">
          <button className="btn btn-outline-secondary btn-sm" onClick={handleReset} disabled={saving || !hasSaved}>
            <RotateCcw size={15} className="me-1" /> Reset to default
          </button>
          <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving || !dirty}>
            <Save size={15} className="me-1" /> {saving ? 'Saving…' : 'Save layout'}
          </button>
        </div>
      </div>

      {error && <div className="alert alert-danger py-2">{error}</div>}
      {notice && <div className="alert alert-success py-2">{notice}</div>}

      {/* Scope / retailer selectors */}
      <div className="row g-3 mb-3">
        <div className="col-sm-6 col-md-4">
          <label className="form-label small fw-semibold">Applies to</label>
          <select
            className="form-select form-select-sm"
            value={retailerId === null ? 'universal' : String(retailerId)}
            onChange={(e) => setRetailerId(e.target.value === 'universal' ? null : Number(e.target.value))}
          >
            <option value="universal">Universal (all retailers)</option>
            {retailers.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}{r.isPartner ? ' (partner)' : ''}{r.id === 1 ? ' — Trusscore' : ''}
              </option>
            ))}
          </select>
        </div>
        <div className="col-sm-6 col-md-4">
          <label className="form-label small fw-semibold">Table</label>
          <select
            className="form-select form-select-sm"
            value={scope}
            onChange={(e) => setScope(e.target.value as ColumnLayoutScope)}
          >
            {SCOPES.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        </div>
        <div className="col-md-4 d-flex align-items-end">
          <span className={`badge ${hasSaved ? 'text-bg-info' : 'text-bg-light border'}`}>
            {hasSaved ? 'Custom layout saved' : 'Using default layout'}
          </span>
        </div>
      </div>

      <div className="row g-4">
        {/* Editor */}
        <div className="col-lg-7">
          <div className="table-responsive">
            <table className="table table-sm align-middle">
              <thead>
                <tr>
                  <th style={{ width: 104 }}>Order</th>
                  <th>Column</th>
                  <th>Heading override</th>
                  <th style={{ width: 110 }}>Align</th>
                  <th className="text-center" style={{ width: 56 }} title="Browser">Web</th>
                  <th className="text-center" style={{ width: 56 }} title="Print">Print</th>
                  <th className="text-center" style={{ width: 56 }}>PDF</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it, idx) => {
                  const meta = metaById.get(it.id);
                  return (
                    <tr
                      key={it.id}
                      onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; if (overIndex !== idx) setOverIndex(idx); }}
                      onDrop={(e) => { e.preventDefault(); onRowDrop(idx); }}
                      style={{
                        opacity: dragIndex === idx ? 0.4 : 1,
                        boxShadow: overIndex === idx && dragIndex !== null && dragIndex !== idx
                          ? 'inset 0 2px 0 0 var(--bs-primary, #0d6efd)' : undefined,
                      }}
                    >
                      <td>
                        <div className="d-flex align-items-center gap-1">
                          <span
                            draggable
                            onDragStart={(e) => { setDragIndex(idx); e.dataTransfer.effectAllowed = 'move'; try { e.dataTransfer.setData('text/plain', String(idx)); } catch { /* IE */ } }}
                            onDragEnd={() => { setDragIndex(null); setOverIndex(null); }}
                            title="Drag to reorder"
                            aria-label="Drag to reorder"
                            style={{ cursor: 'grab', color: '#9ca3af', display: 'inline-flex' }}
                          >
                            <GripVertical size={16} />
                          </span>
                          <div className="btn-group btn-group-sm" role="group">
                            <button className="btn btn-outline-secondary" onClick={() => move(idx, -1)} disabled={idx === 0} aria-label="Move up">
                              <ArrowUp size={13} />
                            </button>
                            <button className="btn btn-outline-secondary" onClick={() => move(idx, 1)} disabled={idx === items.length - 1} aria-label="Move down">
                              <ArrowDown size={13} />
                            </button>
                          </div>
                        </div>
                      </td>
                      <td>
                        {it.barcode ? (
                          <>
                            <div className="fw-semibold d-flex align-items-center gap-1">
                              <Barcode size={14} /> Barcode
                              <button
                                type="button"
                                className="btn btn-sm btn-link text-danger p-0 ms-auto"
                                title="Remove barcode column"
                                onClick={() => removeItem(it.id)}
                              >
                                <Trash2 size={13} />
                              </button>
                            </div>
                            <div className="d-flex flex-wrap gap-1 mt-1">
                              <select
                                className="form-select form-select-sm"
                                style={{ maxWidth: 150 }}
                                value={it.barcode.symbology}
                                onChange={(e) => updateBarcode(it.id, { symbology: e.target.value as BarcodeSymbology })}
                              >
                                {BARCODE_SYMBOLOGIES.map((s) => (
                                  <option key={s.value} value={s.value}>{s.label}</option>
                                ))}
                              </select>
                              <select
                                className="form-select form-select-sm"
                                style={{ maxWidth: 150 }}
                                value={it.barcode.sourceFieldKey}
                                onChange={(e) => updateBarcode(it.id, { sourceFieldKey: e.target.value })}
                                title="Value to encode"
                              >
                                {sourceFieldOptions.map((o) => (
                                  <option key={o.key} value={o.key}>{o.label}</option>
                                ))}
                              </select>
                              <span className="small text-muted align-self-center">bar height px</span>
                              <input
                                type="number"
                                className="form-control form-control-sm"
                                style={{ width: 72 }}
                                min={20}
                                max={400}
                                value={it.barcode.heightPx ?? 36}
                                onChange={(e) => updateBarcode(it.id, { heightPx: parseInt(e.target.value, 10) || 36 })}
                                title="Bar height (px)"
                              />
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="fw-semibold">{meta?.heading || it.id}</div>
                            <code className="small text-muted">{it.id}</code>
                          </>
                        )}
                      </td>
                      <td>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          placeholder={meta?.heading || ''}
                          value={it.headingOverride ?? ''}
                          onChange={(e) => update(it.id, { headingOverride: e.target.value || null })}
                        />
                      </td>
                      <td>
                        <select
                          className="form-select form-select-sm"
                          value={it.align ?? ''}
                          onChange={(e) => update(it.id, { align: (e.target.value || null) as ColumnAlign | null })}
                        >
                          {ALIGN_OPTIONS.map((a) => (
                            <option key={a.value} value={a.value}>{a.label}</option>
                          ))}
                        </select>
                      </td>
                      <td className="text-center">
                        <input type="checkbox" className="form-check-input" checked={it.visibleBrowser}
                          onChange={(e) => update(it.id, { visibleBrowser: e.target.checked })} />
                      </td>
                      <td className="text-center">
                        <input type="checkbox" className="form-check-input" checked={it.visiblePrint}
                          onChange={(e) => update(it.id, { visiblePrint: e.target.checked })} />
                      </td>
                      <td className="text-center">
                        <input type="checkbox" className="form-check-input" checked={it.visiblePdf}
                          onChange={(e) => update(it.id, { visiblePdf: e.target.checked })} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
            <button type="button" className="btn btn-outline-primary btn-sm" onClick={addBarcodeColumn}>
              <Barcode size={14} className="me-1" /> Add barcode column
            </button>
          </div>
          <p className="text-muted small mt-2">
            Column list reflects the currently loaded catalog's custom fields. Price columns appear only on the
            Bill of Materials and only when an estimate shows pricing. Barcode columns encode the chosen field's
            value (Code 128 for alphanumeric SKUs; UPC-A/EAN for numeric).
          </p>
        </div>

        {/* Live preview */}
        <div className="col-lg-5">
          <div className="card">
            <div className="card-header d-flex align-items-center gap-2 py-2">
              <Eye size={15} /> <span className="fw-semibold small">Live preview (browser view)</span>
            </div>
            <div className="card-body">
              <RenderTable table={previewTable} ctx={previewCtx} />
              <p className="text-muted small mb-0 mt-2">
                Sample data. Print/PDF-only columns are hidden here exactly as they would be on screen.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminColumnLayout;
